import Axios, { AxiosInstance } from 'axios';
import React from 'react';

import { apiConfig } from '../../../config/server/server.index';

import { ConsoleAdapter } from './adapters/ConsoleAdapter';
import { DatadogAdapter } from './adapters/DatadogAdapter';
import { LogLevel } from './LogLevels';

export interface Adapter {
  getName(): string;
  log(message: string, level: LogLevel, args?: LoggingContext): void;
}

export interface LoggingContext {
  error?: Error;
  info?: React.ErrorInfo;
}

class LlsLogger {
  private readonly adapters: { [idx: string]: Adapter };

  constructor() {
    this.adapters = {};
  }

  addAdapter(adapter: Adapter) {
    this.adapters[adapter.getName()] = adapter;
    this.debug(`[Adapter connected]: ${adapter.getName()}`);
  }

  debug(msg: string, args?: LoggingContext) {
    this.log(msg, 'debug', args);
  }

  info(msg: string, args?: LoggingContext) {
    this.log(msg, 'info', args);
  }

  warn(msg: string, args?: LoggingContext) {
    this.log(msg, 'warn', args);
  }

  error(msg: string, args?: LoggingContext) {
    this.log(msg, 'error', args);
  }

  fatal(msg: string, args?: LoggingContext) {
    this.log(msg, 'fatal', args);
  }

  private log(msg: string, type: LogLevel, args?: LoggingContext) {
    Object.values(this.adapters).forEach(adapter => adapter.log(msg, type, args));
  }
}

function createAxios(): AxiosInstance {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = localStorage.getItem('jwt');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return Axios.create({
    baseURL: apiConfig.logService.base_url,
    headers: headers,
  });
}

const logger = new LlsLogger();
logger.addAdapter(new ConsoleAdapter());
logger.addAdapter(
  new DatadogAdapter(apiConfig.log_level as LogLevel, createAxios(), window.navigator.userAgent),
);

export default logger;
